'use client';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const stats = [
  { value: 22, suffix: '+', label: 'Cities Operational', icon: 'MapPinIcon', color: '#0066FF' },
  { value: 6, suffix: '', label: 'Partner Companies', icon: 'UsersIcon', color: '#075DDF' },
  { value: 5500, suffix: '+', label: 'Trips Every Day', icon: 'TruckIcon', color: '#075DDF', display: '5,500+' },
  { value: 3, suffix: 'M+', label: 'Successful Trips', icon: 'CheckCircleIcon', color: '#0066FF' },
];

function useCountUp(target: number, duration = 1400, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

function StatCard({ stat, index, active }: { stat: typeof stats[0]; index: number; active: boolean }) {
  const count = useCountUp(stat.value, 1200 + index * 100, active);

  const display = stat.display
    ? (active ? stat.display : '0')
    : (active ? `${count.toLocaleString()}${stat.suffix}` : '0');

  return (
    <div className={`group flex flex-col sm:flex-row items-start sm:items-center gap-4 p-8 hover-lift transition-all duration-500`}
      style={{ animationDelay: `${index * 0.1}s` }}>
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 icon-glow group-hover:scale-110 transition-transform duration-500"
        style={{ background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`, border: `1px solid ${stat.color}30` }}
      >
        <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={22} style={{ color: stat.color }} />
      </div>
      <div>
        <div className={`font-display text-3xl font-semibold text-[#0F172A] leading-none tabular-nums ${active ? 'counter-pop' : ''}`}>
          {display}
        </div>
        <div className="text-sm text-[#64748B] font-medium mt-1">{stat.label}</div>
        <div className="mt-2 h-0.5 w-0 group-hover:w-full transition-all duration-500 rounded-full" style={{ background: `linear-gradient(90deg, ${stat.color}, transparent)` }} />
      </div>
    </div>
  );
}

export default function StatsBar() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setActive(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="bg-white border-b border-[rgba(15,23,42,0.06)] relative overflow-hidden">
      {/* Subtle circuit bg */}
      <div className="absolute inset-0 circuit-bg opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-[rgba(15,23,42,0.06)]">
          {stats.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} index={i} active={active} />
          ))}
        </div>
      </div>
    </div>
  );
}